// 浏览器语音识别可能使用浏览器供应商的联网服务，不发送音频到 Merry。
export interface SpeechResultEvent {
  resultIndex: number;
  results: ArrayLike<{ isFinal: boolean; length: number; [index: number]: { transcript: string } }>;
}
export interface BrowserSpeechRecognition {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((event: SpeechResultEvent) => void) | null;
  onerror: ((event: { error: string }) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}
export type SpeechRecognitionConstructor = new () => BrowserSpeechRecognition;
declare global {
  interface Window {
    SpeechRecognition?: SpeechRecognitionConstructor;
    webkitSpeechRecognition?: SpeechRecognitionConstructor;
  }
}
export function browserSpeechConstructor(): SpeechRecognitionConstructor | undefined {
  return typeof window === "undefined" ? undefined : window.SpeechRecognition ?? window.webkitSpeechRecognition;
}
export function mergeSpeechTranscript(draft: string, transcript: string) {
  const text = transcript.trim();
  if (!text) return draft;
  return draft + (draft && !/\s$/.test(draft) ? " " : "") + text;
}
export function speechErrorMessage(error: string) {
  const messages: Record<string, string> = {
    "not-allowed": "麦克风权限被拒绝，请在浏览器设置中允许使用麦克风。",
    "service-not-allowed": "浏览器语音识别服务不可用，请继续使用键盘输入。",
    "audio-capture": "未找到可用麦克风，请检查设备连接。",
    "no-speech": "没有识别到语音，请再试一次。",
    network: "语音识别网络失败，请检查网络后重试。",
    "language-not-supported": "浏览器不支持当前识别语言，请使用键盘输入。",
  };
  return messages[error] ?? "语音识别失败，请重试或使用键盘输入。";
}
export class BrowserSpeechInput {
  private active = false;
  private delivered = new Set<number>();
  constructor(private recognition: BrowserSpeechRecognition, private callbacks: {
    onFinal: (text: string) => void; onEnd: () => void; onError: (message: string) => void;
  }) {
    recognition.lang = "zh-CN";
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.onresult = (event) => {
      if (!this.active) return;
      for (let index = event.resultIndex; index < event.results.length; index++) {
        const result = event.results[index];
        if (!result?.isFinal || this.delivered.has(index)) continue;
        this.delivered.add(index);
        if (result[0]?.transcript.trim()) callbacks.onFinal(result[0].transcript);
      }
    };
    recognition.onerror = (event) => {
      if (!this.active) return;
      this.active = false;
      if (event.error !== "aborted") callbacks.onError(speechErrorMessage(event.error));
      callbacks.onEnd();
    };
    recognition.onend = () => {
      if (!this.active) return;
      this.active = false;
      callbacks.onEnd();
    };
  }
  start() { this.active = true; this.recognition.start(); }
  stop() {
    const wasActive = this.active;
    this.active = false;
    this.recognition.onresult = null;
    this.recognition.onerror = null;
    this.recognition.onend = null;
    try { this.recognition.abort(); } catch { /* 未启动的浏览器实例无需再次取消。 */ }
    if (wasActive) this.callbacks.onEnd();
  }
}
